"use client";

import { notFound } from "next/navigation";
import { Heading, Link, Spinner, Text } from "ui-components";

import { FriendlyFeaturedOrganizationTabs } from "@/components/homepage/FriendlyFeaturedOrganizationTabs";
import { ProfileTabsEnum } from "@/src/@types/Profile";
import { MyGworkEventsWP } from "@/src/components/events/MyGworkEventsWP";
import { MyGworkNewsWP } from "@/src/components/news/MyGworkNewsWP";
import {
  FriendlyOrganizationResponseEntity,
  useUserGetDashboard,
} from "@/src/hooks/api/generated";

import { LatestJobs } from "./LatestJobs";
import MemberInfoCard from "./MemberInfoCard";
import { UserCVUploadPrompt } from "./UserCVUploadPrompt";

export function UserProfileHome(): JSX.Element {
  const { data, isLoading } = useUserGetDashboard();

  if (isLoading) {
    return (
      <div className="flex justify-center mt-40">
        <Spinner size="lg" intent="brand-primary" />
      </div>
    );
  }

  if (!data) {
    return notFound();
  }

  return (
    <div className="w-full flex flex-col lg:flex-row gap-y-10 lg:gap-x-10 px-4 lg:px-14">
      <UserCVUploadPrompt />
      {/* Left Section */}
      <div className="w-full lg:w-3/5 flex flex-col gap-y-10 mr-10">
        <LatestJobs />
        {!!data.upcomingEvents.length && (
          <MyGworkEventsWP
            title="Upcoming Events"
            events={data.upcomingEvents}
            slidesToShow={2}
            spacing="small"
          />
        )}
        {!!data.latestNews.length && (
          <MyGworkNewsWP
            title="Latest News"
            news={data.latestNews}
            slidesToShow={3}
            spacing="small"
          />
        )}
        {!!data.friendlyOrganizations.length && (
          <FriendlyFeaturedOrganizationTabs
            organization={
              data
                .friendlyOrganizations[0] as FriendlyOrganizationResponseEntity
            }
            spacing="small"
            align="left"
            header="inline"
          />
        )}
      </div>

      {/* Right Section */}
      <div className="w-full lg:w-1/3 flex flex-col gap-y-10">
        <div className="flex flex-col gap-y-5">
          <Heading variant="h2">About you</Heading>
          <MemberInfoCard user={data.profile} isShowAllDetails />
        </div>

        <div className="flex flex-col gap-y-5">
          <Heading variant="h2">Connections</Heading>
          <div className="bg-gray-100 w-full p-5 rounded-3xl flex flex-col gap-y-2.5 justify-start items-center relative">
            <Heading intent="themePrimary">{data.totalConnections}</Heading>
            <Text variant="large">Connections</Text>
            <Link href={`/profile/${ProfileTabsEnum.MY_CONNECTIONS}`}>
              View My Connections
            </Link>
          </div>
        </div>
        <div className="flex flex-col gap-y-5">
          <Heading variant="h2">People you may know</Heading>
          {data?.peopleYouMayKnow?.map((member) => (
            <MemberInfoCard
              hasConnectButton
              key={member.id}
              user={member}
              isShowAllDetails
            />
          ))}
        </div>
      </div>
    </div>
  );
}
